/**
 * Swing/twist decomposition — the split ADR-028 E0 requires before any hinge
 * corrective may run: a corrective driven off a raw local rotation that still
 * contains twist (forearm pronation, thigh roll) creases the arm when the
 * wrist turns. The bend must be a pure 1-DOF angle, so the rotation is torn
 * into the part ABOUT a declared axis (twist) and the remainder (swing).
 *
 * Convention: `q = swing · twist` — twist is applied FIRST, about `axis`
 * expressed in q's own (pre-rotation) frame; swing carries what remains.
 * The decomposition is the standard projection: the twist is the normalized
 * projection of q's vector part onto the axis, keeping w.
 *
 * Degenerate case, handled without a throw: a rotation of exactly 180° about
 * an axis perpendicular to `axis` projects to the zero quaternion — there the
 * twist is genuinely zero and ALL of q is swing. (quatNormalize would throw;
 * the zero projection is not a data error here, it is the answer.)
 *
 * Depends on: math/quat, math/vec3. Depended on by: correctives/hinge-correctives
 * (θ extraction), the E0 twist-routing seam.
 */
import { quatMultiply, quatInvertUnit, type Quat } from './quat.js';
import { vec3Normalize, type Vec3Data } from './vec3.js';

export interface SwingTwist {
  /** Rotation about `axis`, applied first: q = swing · twist. */
  readonly twist: Quat;
  /** The remainder: q · twist⁻¹. */
  readonly swing: Quat;
  /** Signed twist angle about `axis`, radians, in (−π, π]. */
  readonly twistRadians: number;
}

const EPSILON = 1e-12;

/** Decompose a UNIT quaternion about a non-zero axis (normalized here, loudly). */
export function swingTwist(q: Quat, axis: Vec3Data): SwingTwist {
  const a = vec3Normalize(axis);
  const proj = q.x * a.x + q.y * a.y + q.z * a.z;
  const len = Math.hypot(proj, q.w);
  if (len < EPSILON) {
    // 180° about a perpendicular axis: zero twist, everything is swing.
    return { twist: { x: 0, y: 0, z: 0, w: 1 }, swing: q, twistRadians: 0 };
  }
  let tx = (a.x * proj) / len;
  let ty = (a.y * proj) / len;
  let tz = (a.z * proj) / len;
  let tw = q.w / len;
  // TWO canonicalizations, and only these two, make the angle a function of
  // the ROTATION rather than its encoding (Codex P2, #102 rounds two+three):
  // (1) scalar-sign flip — q and −q become the SAME components, so no later
  // branch can see the encoding (an earlier projection-based tie flip made
  // the tiny scalar negative and pushed atan2 just past +π); (2) the ±π seam
  // is ONE rotation, and its canonical name is +π — an angle within TIE_EPS
  // of −π (reachable when w ≈ 0 with a negative projection) folds to +π,
  // costing at most 2·ε of a rotation that far from every corrective ramp.
  if (tw < 0) {
    tx = -tx;
    ty = -ty;
    tz = -tz;
    tw = -tw;
  }
  const twist: Quat = { x: tx, y: ty, z: tz, w: tw };
  const swing = quatMultiply(q, quatInvertUnit(twist));
  const TIE_EPS = 1e-9;
  let twistRadians = 2 * Math.atan2(tx * a.x + ty * a.y + tz * a.z, tw);
  if (twistRadians <= -Math.PI + TIE_EPS) twistRadians = Math.PI;
  return { twist, swing, twistRadians };
}

/** The signed rotation angle of a unit quaternion, radians, in [0, 2π) folded to [0, π] by shortest arc. */
export function rotationAngle(q: Quat): number {
  return 2 * Math.acos(Math.min(1, Math.abs(q.w)));
}

/**
 * The delta a bone's local rotation has taken from bind, expressed in the
 * bone's own bind-local frame: `current = bind · delta` ⇒ `delta = bind⁻¹ · current`.
 * This is the frame hinge/limb axes are declared in (a family prior, same
 * pattern as jointFrames) — decompositions of this delta read those axes directly.
 */
export function localDeltaFromBind(bindLocal: Quat, currentLocal: Quat): Quat {
  return quatMultiply(quatInvertUnit(bindLocal), currentLocal);
}

/** Twist angle of `q` about `axis`, degrees — the 1-DOF reading correctives drive on. */
export function twistAngleDeg(q: Quat, axis: Vec3Data): number {
  return (swingTwist(q, axis).twistRadians * 180) / Math.PI;
}
